import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import {
  roleAssignedEmployeesService,
  type RoleAssignedEmployee,
  type RoleAssignedEmployeesStatusFilter,
  ROLE_ASSIGNED_EMPLOYEES_LIST_DEFAULT_PAGE_SIZE,
  ROLE_ASSIGNED_EMPLOYEES_STATUS_FILTER,
} from '@/api/domains/authorization';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


interface AssignedEmployeesFiltersState {
  status: RoleAssignedEmployeesStatusFilter;
}

export interface UseRoleAssignedEmployeesListResult {
  rows: RoleAssignedEmployee[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: AssignedEmployeesFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<AssignedEmployeesFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<RoleAssignedEmployee>[];
  actions: TableAction<RoleAssignedEmployee>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  canView: boolean;
  /** Texto de búsqueda debounced vigente — se reenvía a la exportación para que el archivo
   * refleje el mismo filtro que el listado. */
  debouncedSearchText: string;
}

export function useRoleAssignedEmployeesList(): UseRoleAssignedEmployeesListResult {
  const { t: translate } = useTranslation();
  const { showApiError, openSidePanel } = useUI();
  const { isLoaded, canView } = usePagePermissions();

  // El AbortSignal que entrega usePaginatedList NO se propaga al request a propósito:
  // abortarlo generaría TaskCanceledException en el backend durante los doble-montajes de
  // React.StrictMode en desarrollo (mismo criterio que useCatalogLoader.ts). El resto de las
  // pestañas de esta página tampoco cancela sus requests.
  // La protección contra resultados obsoletos se mantiene: usePaginatedList sigue abortando el
  // signal y descarta la respuesta de cualquier fetch superado antes de escribir en el estado.
  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AssignedEmployeesFiltersState>,
    ): Promise<PaginatedFetchResult<RoleAssignedEmployee> | null> => {
      const response = await roleAssignedEmployeesService.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        status: params.filters.status,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('authorization:assigned.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
  } = usePaginatedList<RoleAssignedEmployee, AssignedEmployeesFiltersState>({
    fetchFn,
    initialFilters: { status: ROLE_ASSIGNED_EMPLOYEES_STATUS_FILTER.ALL },
    initialPageSize: ROLE_ASSIGNED_EMPLOYEES_LIST_DEFAULT_PAGE_SIZE,
    enabled: isLoaded && canView,
    orderByMap: {
      rut: 'employeeTaxIdentificationNumber',
      name: 'employeeName',
      webRolesCount: 'webRolesCount',
      mobileRolesCount: 'mobileRolesCount',
      status: 'status',
    },
    screenCode: ROUTE_DEFINITIONS.PERFILES_USUARIOS.code,
  });

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('authorization:assigned.filters.status'),
      options: [
        { value: ROLE_ASSIGNED_EMPLOYEES_STATUS_FILTER.ALL, label: translate('authorization:assigned.filters.all') },
        { value: ROLE_ASSIGNED_EMPLOYEES_STATUS_FILTER.ENABLED, label: translate('authorization:assigned.filters.active') },
        { value: ROLE_ASSIGNED_EMPLOYEES_STATUS_FILTER.DISABLED, label: translate('authorization:assigned.filters.inactive') },
      ],
    },
  ], [translate]);

  const columns: TableColumn<RoleAssignedEmployee>[] = useMemo(
    () => [
      {
        key: 'rut',
        header: translate('authorization:assigned.columns.rut'),
        render: (row: RoleAssignedEmployee) => row.rut,
        width: 130,
        sortable: true,
      },
      {
        key: 'name',
        header: translate('authorization:assigned.columns.name'),
        render: (row: RoleAssignedEmployee) => row.name,
        sortable: true,
      },
      {
        key: 'webRolesCount',
        header: translate('authorization:assigned.columns.webRolesCount'),
        render: (row: RoleAssignedEmployee) => row.webRolesCount,
        width: 150,
        align: 'center',
        sortable: true,
      },
      {
        key: 'mobileRolesCount',
        header: translate('authorization:assigned.columns.mobileRolesCount'),
        render: (row: RoleAssignedEmployee) => row.mobileRolesCount,
        width: 150,
        align: 'center',
        sortable: true,
      },
      {
        key: 'status',
        header: translate('authorization:assigned.columns.status'),
        render: (row: RoleAssignedEmployee) => translate(`authorization:assigned.status.${row.status}`),
        width: 110,
        sortable: true,
      },
    ],
    [translate],
  );

  const handleViewDetail = useCallback(
    (row: RoleAssignedEmployee) => {
      openSidePanel({
        component: 'EmployeeRolesDetailPanel',
        title: translate('authorization:assigned.detail.title'),
        props: { employee: row },
      });
    },
    [openSidePanel, translate],
  );

  const actions: TableAction<RoleAssignedEmployee>[] = useMemo(
    () => [
      {
        id: 'view-detail',
        icon: 'eye',
        label: translate('common:tableActions.detail'),
        onClick: handleViewDetail,
      },
    ],
    [translate, handleViewDetail],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    canView,
    debouncedSearchText,
  };
}

export default useRoleAssignedEmployeesList;
